import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS } from '../../constants/firebase';
import { X, Check, RefreshCw, FileText, Search, Loader2 } from 'lucide-react';

export interface CtReviewVariable {
  id: string;
  varName: string;
  questionText: string;
  ct_codelist_ref?: string;
  ct_codelist_version?: string;
}

export interface CtCodelistOption {
  oid: string;
  name: string;
  label: string;
}

export interface CtDecision {
  varId: string;
  ct_codelist_ref?: string;
  ct_codelist_version?: string;
  ct_match_confidence: 'user_confirmed' | 'sponsor_defined';
}

interface CtReviewModalProps {
  formName: string;
  variables: CtReviewVariable[];
  ctVersion: string;
  onClose: () => void;
  onDecision: (decision: CtDecision) => void;
}

export default function CtReviewModal({
  formName,
  variables,
  ctVersion,
  onClose,
  onDecision,
}: CtReviewModalProps) {
  const [codelists, setCodelists] = useState<CtCodelistOption[]>([]);
  const [loadingCodelists, setLoadingCodelists] = useState(false);
  const [codelistError, setCodelistError] = useState<string | null>(null);
  const [swapVarId, setSwapVarId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingCodelists(true);
    setCodelistError(null);
    const fn = httpsCallable(functions, CLOUD_FUNCTIONS.LIST_CT_CODELISTS);
    fn({ ctVersion })
      .then((result) => {
        if (cancelled) return;
        const data = result.data as { codelists: CtCodelistOption[] };
        setCodelists(data.codelists || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCodelistError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoadingCodelists(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ctVersion]);

  const codelistByOid = useMemo(() => {
    const map = new Map<string, CtCodelistOption>();
    for (const cl of codelists) map.set(cl.oid, cl);
    return map;
  }, [codelists]);

  const filteredCodelists = useMemo(() => {
    if (!searchTerm.trim()) return codelists;
    const needle = searchTerm.trim().toLowerCase();
    return codelists.filter(
      (cl) =>
        cl.oid.toLowerCase().includes(needle) ||
        cl.name.toLowerCase().includes(needle) ||
        cl.label.toLowerCase().includes(needle)
    );
  }, [codelists, searchTerm]);

  const handleAccept = (v: CtReviewVariable) => {
    if (!v.ct_codelist_ref) return;
    onDecision({
      varId: v.id,
      ct_codelist_ref: v.ct_codelist_ref,
      ct_codelist_version: v.ct_codelist_version || ctVersion,
      ct_match_confidence: 'user_confirmed',
    });
  };

  const handleSponsorDefined = (v: CtReviewVariable) => {
    onDecision({
      varId: v.id,
      ct_codelist_ref: undefined,
      ct_codelist_version: undefined,
      ct_match_confidence: 'sponsor_defined',
    });
  };

  const handleSwapPick = (v: CtReviewVariable, chosen: CtCodelistOption) => {
    onDecision({
      varId: v.id,
      ct_codelist_ref: chosen.oid,
      ct_codelist_version: ctVersion,
      ct_match_confidence: 'user_confirmed',
    });
    setSwapVarId(null);
    setSearchTerm('');
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Review CT Codelists — {formName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {variables.length} picklist{variables.length === 1 ? '' : 's'} pending review · CT version {ctVersion}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {variables.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-8">
              No items pending review.
            </div>
          )}
          {variables.map((v) => {
            const proposed = v.ct_codelist_ref
              ? codelistByOid.get(v.ct_codelist_ref)
              : undefined;
            const isSwapping = swapVarId === v.id;
            return (
              <div
                key={v.id}
                className="border border-gray-200 rounded-md p-3 bg-white"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-gray-700">{v.varName}</div>
                    <div className="text-sm text-gray-900 mt-0.5">{v.questionText}</div>
                    <div className="mt-2 text-xs">
                      <span className="text-gray-500">Proposed match: </span>
                      {v.ct_codelist_ref ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-semibold">
                          {v.ct_codelist_ref}
                          {proposed ? ` · ${proposed.name}` : ''}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">none</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {v.ct_codelist_ref && (
                      <button
                        onClick={() => handleAccept(v)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700"
                      >
                        <Check className="h-3.5 w-3.5" /> Accept
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSwapVarId(isSwapping ? null : v.id);
                        setSearchTerm('');
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Swap
                    </button>
                    <button
                      onClick={() => handleSponsorDefined(v)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <FileText className="h-3.5 w-3.5" /> Sponsor-defined
                    </button>
                  </div>
                </div>

                {isSwapping && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Search className="h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search codelists by OID, name, or label..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        autoFocus
                      />
                    </div>
                    {loadingCodelists && (
                      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading codelists...
                      </div>
                    )}
                    {codelistError && (
                      <div className="text-xs text-red-600 py-2">
                        Failed to load codelists: {codelistError}
                      </div>
                    )}
                    {!loadingCodelists && !codelistError && (
                      <div className="max-h-52 overflow-y-auto border border-gray-200 rounded">
                        {filteredCodelists.length === 0 ? (
                          <div className="text-xs text-gray-500 p-3 text-center">
                            No codelists match.
                          </div>
                        ) : (
                          filteredCodelists.map((cl) => (
                            <button
                              key={cl.oid}
                              onClick={() => handleSwapPick(v, cl)}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50 border-b border-gray-100 last:border-0"
                            >
                              <span className="font-mono text-xs text-primary-700">{cl.oid}</span>
                              <span className="ml-2 font-semibold text-gray-900">{cl.name}</span>
                              <span className="ml-2 text-xs text-gray-500">{cl.label}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
